import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const method = params.get("method");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    // PayPal flow: payment already captured server-side, just fetch the confirmed order
    if (method === "paypal" && orderId) {
      api.get(`/checkout/order/${orderId}`)
        .then(({ data }) => {
          setOrder(data);
          if (data.payment_status === "paid") {
            if (!cleared.current) { clearCart(); cleared.current = true; }
            setState("success");
          } else { setState("failed"); }
        })
        .catch(() => setState("failed"));
      return;
    }
    // Stripe flow: poll status
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 10) { setState("timeout"); return; }
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setOrder(data.order);
        if (data.payment_status === "paid") {
          if (!cleared.current) { clearCart(); cleared.current = true; }
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, orderId, method]);

  return (
    <div className="max-w-[700px] mx-auto px-6 py-24 text-center" data-testid="order-success-page">
      {state === "checking" && (
        <><Loader2 className="mx-auto text-[#FF3B30] animate-spin mb-6" size={56} />
          <h1 className="font-display text-4xl uppercase tracking-tight">Confirming Payment…</h1>
          <p className="text-zinc-400 mt-3">Hold tight while we verify your order.</p></>
      )}
      {state === "success" && (
        <div data-testid="order-confirmed">
          <CheckCircle2 className="mx-auto text-[#EAB308] mb-6" size={64} />
          <h1 className="font-display text-5xl uppercase tracking-tighter">Order <span className="text-[#FF3B30]">Confirmed!</span></h1>
          <p className="text-zinc-300 mt-4">Thanks for repping Monster Truck Lord. A confirmation is on its way.</p>
          {order && (
            <div className="border border-zinc-800 bg-zinc-900 p-6 mt-8 text-left">
              <div className="flex justify-between text-sm mb-2"><span className="text-zinc-400 uppercase tracking-widest text-xs">Total Paid</span><span className="font-display text-xl text-[#FF3B30]">${order.total?.toFixed(2)}</span></div>
              <div className="text-xs text-zinc-500 uppercase tracking-widest">{order.items?.length} item(s) · Shipping to {order.shipping?.city}</div>
            </div>
          )}
          <div className="mt-8 flex gap-4 justify-center">
            <Link to="/shop" className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">Keep Shopping</Link>
            <Link to="/account" className="border border-white text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">My Orders</Link>
          </div>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <><XCircle className="mx-auto text-[#FF3B30] mb-6" size={64} />
          <h1 className="font-display text-4xl uppercase tracking-tight">{state === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
          <p className="text-zinc-400 mt-3">{state === "timeout" ? "Your payment is taking a moment. Check your account shortly." : "Something went wrong. Your cart is still saved."}</p>
          <Link to="/cart" className="mt-8 inline-block bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">Back To Cart</Link></>
      )}
    </div>
  );
}
